import {
  Badge,
  HStack,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FiEdit2 } from "react-icons/fi";
import { KYDTicketTypeAccessCode } from "@common/interfaces/KYDTicket";
import dayjs, { Dayjs } from "dayjs";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";

const formatDate = (date: Dayjs) => {
  return date.format("MMM D h:mmA");
};

const formatFromToDate = (tz: string, from?: string, to?: string) => {
  if (to && from) {
    const toDayjs = dayjs(to).tz(tz);
    const fromDayjs = dayjs(from).tz(tz);
    return (
      <>
        <Text>
          {formatDate(fromDayjs)} - {formatDate(toDayjs)}
        </Text>
      </>
    );
  }

  if (to) {
    const toDayjs = dayjs(to).tz(tz);
    return (
      <>
        <Text>before</Text>
        <Text>{formatDate(toDayjs)}</Text>
      </>
    );
  }

  if (from) {
    const fromDayjs = dayjs(from).tz(tz);
    return (
      <>
        <Text>after</Text>
        <Text>{formatDate(fromDayjs)}</Text>
      </>
    );
  }

  return <Text>Anytime</Text>;
};

const isHoldAccessCode = (accessCode: KYDTicketTypeAccessCode) => {
  return accessCode.ticket_type_ids.some((id) => id.startsWith("HL"));
};

export const AccessCodesTable = ({
  accessCodes,
  onEdit,
  loading,
  readOnly = false,
  timezone,
  isDisabled = false,
  realTimeMetrics,
  ...props
}: {
  accessCodes: KYDTicketTypeAccessCode[];
  onEdit: (accessCode: KYDTicketTypeAccessCode) => void;
  readOnly: boolean;
  timezone?: string;
  isDisabled?: boolean;
  loading: any;
  realTimeMetrics?: KYDAdminEvent["realtime_metrics"];
}) => (
  <Table {...props}>
    <Thead>
      <Tr>
        <Th
          position={{ base: "sticky", md: "static" }}
          left={{ base: 0, md: undefined }}
          zIndex={{ base: 1, md: undefined }}
          bg={{ base: "white", md: undefined }}
          boxShadow={{
            base: "1px 0px var(--chakra-colors-gray-200)",
            md: "none",
          }}
        >
          Code
        </Th>
        <Th>Ticket Types</Th>
        <Th>Uses</Th>
        <Th>Revenue</Th>
        <Th>Valid</Th>
        <Th>Status</Th>
        <Th></Th>
      </Tr>
    </Thead>
    <Tbody>
      {accessCodes.map((accessCode: KYDTicketTypeAccessCode) => (
        <Tr key={accessCode.id}>
          <Td
            position={{ base: "sticky", md: "static" }}
            left={{ base: 0, md: undefined }}
            zIndex={{ base: 1, md: undefined }}
            bg={{ base: "white", md: undefined }}
            boxShadow={{
              base: "1px 0px var(--chakra-colors-gray-200)",
              md: "none",
            }}
          >
            <Text fontWeight="medium">{accessCode.code}</Text>
            <Text fontSize="xs" color="gray.500">
              {accessCode.created_at
                ? `Created: ${dayjs(accessCode.created_at).format("MMM D")}`
                : ""}
            </Text>
          </Td>
          <Td>
            <HStack>
              <Text>{accessCode.ticket_type_ids.length}</Text>
              {isHoldAccessCode(accessCode) && (
                <Badge variant="subtle" size="sm">
                  Hold
                </Badge>
              )}
            </HStack>
          </Td>
          <Td>
            <Text>
              {realTimeMetrics?.access_codes?.[accessCode.id]?.uses ?? "N/A"}
            </Text>
          </Td>
          <Td>
            <Text>
              {realTimeMetrics?.access_codes?.[accessCode.id]
                ?.display_revenue ?? "N/A"}
            </Text>
          </Td>
          <Td>
            <Text fontSize="xs">
              {formatFromToDate(
                timezone,
                accessCode.valid_from,
                accessCode.valid_to
              )}
            </Text>
          </Td>
          <Td>
            <Badge colorScheme={accessCode.status === "OPEN" ? "green" : "red"}>
              {accessCode.status}
            </Badge>
          </Td>
          <Td>
            <HStack spacing="1" justifyContent="flex-end">
              {readOnly ? null : (
                <IconButton
                  isDisabled={isDisabled || loading["blocking"]}
                  onClick={() => onEdit(accessCode)}
                  icon={<FiEdit2 fontSize="1.25rem" />}
                  variant="ghost"
                  aria-label="Edit member"
                />
              )}
            </HStack>
          </Td>
        </Tr>
      ))}
    </Tbody>
  </Table>
);
